import { helpers } from '@vuelidate/validators'

const isValidEmailAddress = (value: string) => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return !value || emailRegex.test(String(value).toLowerCase())
}

const isValidFileFormat = (value: string) => {
  if (!value) {
    return true
  }
  const fileFormatRegex = /\.(?:wav|mp3)$/
  if (!value.match(fileFormatRegex)) {
    return false
  }
  return true
}

const isGermanMobileNumber = (value: string) => {
  if (!value) {
    return true
  }
  const germanMobileNumberRegEx = /^01(5|6|7).*$/
  if (!value.match(germanMobileNumberRegEx)) {
    return false
  }
  return true
}

const isValidEuropeanMobileNumber = (value: string) => {
  if (!value) {
    return true
  }

  // German mobile numbers: 015x, 016x, 017x (10-12 digits total with country code)
  const germanMobileRegex = /^(\+49|0049|49|0)1[5-7]\d{8,9}$/

  // Dutch mobile numbers: 06xxxxxxxx (with country code: +31 or 0031)
  const dutchMobileRegex = /^(\+31|0031|31|0)6\d{8}$/

  // French mobile numbers: 06xxxxxxxx or 07xxxxxxxx (with country code: +33 or 0033)
  const frenchMobileRegex = /^(\+33|0033|33|0)[67]\d{8}$/

  // Austrian mobile numbers: 0650-0699, 0664, 0676, 0680, 0681, 0688, 0699 (with country code: +43 or 0043)
  const austrianMobileRegex =
    /^(\+43|0043|43|0)(65[0-9]|66[4]|67[6]|68[0-1]|68[8]|69[9])\d{6,7}$/

  return (
    germanMobileRegex.test(value) ||
    dutchMobileRegex.test(value) ||
    frenchMobileRegex.test(value) ||
    austrianMobileRegex.test(value)
  )
}

const isSubscriberNumber = (value: string) => {
  if (!value) {
    return true
  }
  const subscriberNumberRegex = /^[0-9][0-9]{2,10}$/
  if (!value.match(subscriberNumberRegex)) {
    return false
  }
  return true
}

const isNationalDestinationCode = (value: string) => {
  if (!value) {
    return true
  }
  const isNationalDestinationCodeRegEx = /^0[1-9][0-9]{0,4}$/
  if (!value.match(isNationalDestinationCodeRegEx)) {
    return false
  }
  return true
}

const noDuplicateEntry = (list: string[]) =>
  helpers.withParams({ list }, (value: string) => {
    if (!value) {
      return true
    }
    return !list.includes(value.toLowerCase())
  })

const notPattern = (regex: RegExp, pattern?: string) =>
  helpers.withParams({ pattern }, (value: string) => {
    return !regex.test(value)
  })

const emergencyNumbersRegexByCountry: Record<string, RegExp> = {
  GERMANY: /^(110|112|116117|911)$/,
  AUSTRIA: /^(112|122|128|133|140|141|142|144|147|911)$/,
  FRANCE: /^(112|114|115|196|197|15|17|18|119)$/,
  NETHERLANDS:
    /^(112|08008112|09008844|0343578844|09001844|08000113|144|09001231230|08009050)$/,
  SWITZERLAND: /^(11[2-8]|143|144|145|147)$/,
}

const getEmergencyNumbersRegex = (serviceCountry?: string): RegExp =>
  (serviceCountry && emergencyNumbersRegexByCountry[serviceCountry]) ||
  emergencyNumbersRegexByCountry.GERMANY

const mustContainIpAddress = (value: string) => {
  if (value !== '') {
    // Reference: https://www.regextester.com/96770
    return (
      value
        .split('\n')
        .map((line) =>
          /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/.test(
            line
          )
        )
        .filter((x) => !x).length === 0
    )
  }
  return true
}

const mustContainIp4Or6Address = (value: string) => {
  if (value !== '') {
    // Reference: https://www.regextester.com/96770
    const isIpv4 =
      value
        .split('\n')
        .map((line) =>
          /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/.test(
            line
          )
        )
        .filter((x) => !x).length === 0

    // Reference: https://www.regextester.com/96774
    const isIpv6 =
      value
        .split('\n')
        .map((line) =>
          /^(?:(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){6})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:::(?:(?:(?:[0-9a-fA-F]{1,4})):){5})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){4})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,1}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){3})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,2}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:(?:[0-9a-fA-F]{1,4})):){2})(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,3}(?:(?:[0-9a-fA-F]{1,4})))?::(?:(?:[0-9a-fA-F]{1,4})):)(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,4}(?:(?:[0-9a-fA-F]{1,4})))?::)(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,5}(?:(?:[0-9a-fA-F]{1,4})))?::)(?:(?:[0-9a-fA-F]{1,4})))|(?:(?:(?:(?:(?:(?:[0-9a-fA-F]{1,4})):){0,6}(?:(?:[0-9a-fA-F]{1,4})))?::))))$/.test(
            line
          )
        )
        .filter((x) => !x).length === 0

    return isIpv4 || isIpv6
  }
  return true
}

const mustContainNoPrivateIpAddress = (value: string) => {
  if (value !== '') {
    // Reference: https://www.regextester.com/95489
    return (
      value
        .split('\n')
        .map((line) =>
          /(^192\.168\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])$)|(^172\.([1][6-9]|[2][0-9]|[3][0-1])\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])$)|(^10\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])\.([0-9]|[0-9][0-9]|[0-2][0-5][0-5])$)/.test(
            line
          )
        )
        .filter((x) => x).length === 0
    )
  }
  return true
}

const firstDigitInRange = (blockStart?: number, blockEnd?: number) =>
  helpers.withParams({ blockStart, blockEnd }, (value: string) => {
    if (!value || value.length === 0) {
      return true
    }

    if (
      blockStart === null ||
      blockStart === undefined ||
      blockEnd === null ||
      blockEnd === undefined ||
      blockEnd === 0
    ) {
      return true
    }

    // If range starts at 300, accept 3 as a valid direct dial
    const firstPossibleSequence = String(blockStart).replace(/0+$/, '')

    const lastPossibleSequence = String(blockEnd)

    return (
      firstPossibleSequence <= value &&
      (value < lastPossibleSequence || value.startsWith(lastPossibleSequence))
    )
  })

const mustBePhoneNumber = (value: string) => {
  const TELEPHONE_NUMBER_REGEX = '^(\\+|0|00)[1-9][0-9]{4,19}$'
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    value.match(TELEPHONE_NUMBER_REGEX) !== null
  )
}

const atLeastOneNotEmpty = (value: string) => {
  for (let i = 0; i < value.length; i++) {
    if (value[i] !== '') {
      return true
    }
  }
  return false
}

const notEmptyAndMinLength = (minLength: number) => (value: string) => {
  return value.length >= minLength
}

const atLeastOneDigit = (value: string) => {
  return /.*[0-9].*/.test(value)
}

const atLeastOneCapitalLetter = (value: string) => {
  return /.*[A-Z].*/.test(value)
}

const atLeastOneLowercaseLetter = (value: string) => {
  return /.*[a-z].*/.test(value)
}

const isChecked = (value: string) => {
  return !!value
}

const requiredSelection = helpers.withParams(
  { type: 'required' },
  (value: number | string) => {
    return (
      value !== 0 &&
      value !== '0' &&
      value !== '' &&
      value !== null &&
      value !== undefined
    )
  }
)

const noAccents = (value: string) => {
  //eslint-disable-next-line no-control-regex
  return !/[^\x00-\x7F]+/.test(value)
}

const noConnectMeIncompatibleCharacters = (value: string) => {
  return !/[()[\]{}<>$/\\"\r\n]/.test(value)
}

const notAllowedSpecialCharacters = (value: string) => {
  if (/.((?![a-zA-Z0-9!$/|()=.:+*#{}?]).).*/.test(value)) {
    return false
  }
  return /^(?=.*[.,:;?!$%&=*+~<>,[\]{}()]).*$/.test(value)
}

const atLeastOneSpecialCharacterExcept$ = (value: string) => {
  // Checks if value contain other characters than the allowed ones and '$'
  if (/.((?![a-zA-Z0-9!/|()=.:+*#{}?]).).*/.test(value)) {
    return false
  }
  return /.([!/|()=.:+*#{}?]).*/.test(value)
}

const atLeastOneSpecialCharacterExcept$AndAccents = (value: string) => {
  // Checks if value contain other characters than the allowed ones, '$' and accents
  //eslint-disable-next-line no-control-regex
  if (/((?![a-zA-Z0-9.:?!%&=*+~\-[\]_(){}]).).*/.test(value)) {
    return false
  }
  return /([.:?!%&=*+~\-[\]_(){}]).*/.test(value)
}

const atLeastOneSpecialCharacterExceptHash = (value: string) => {
  // Checks if value contain other characters than the allowed ones and '#'
  if (/.((?![a-zA-Z0-9!/|()=.:+*{}?]).).*/.test(value)) {
    return false
  }
  return /.([!/|()=.:+*{}?]).*/.test(value)
}

const isGigaseMacId = (value: string) => {
  if (!value) {
    return true
  }
  return /([0-9A-Fa-f]{2}){5}([0-9A-Fa-f]{2})(-[0-9A-Za-z]{4}).*/.test(value)
}

const isValidMacAddress = (value: string) => {
  return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value)
}

const validPromptName = (value: string) => {
  // up to 32 characters containing letters (including accented), numbers, dot, underscore,
  // dash, and spaces. Disallows: leading dots or dashes, consecutive dots, and forward slashes (/).
  return /^(?=.{1,32}$)(?![.-])(?!.*\.\.)[A-Za-z0-9À-ÿ\u0370-\u03FF\u0400-\u04FF._\- ]+$/.test(
    value
  )
}

const firstOrLastNameWithTitle = (value: string) => {
  //Checks names without symbols but empty spaces
  return /^(?:[a-zA-Z]+\.\s)?([a-zA-Z0-9\xC0-\uFFFF]+(?:[ \-'][a-zA-Z\xC0-\uFFFF]+)*\.?){1,2}$/.test(
    value
  )
}

const atLeastOneWhiteSpace = (value: string) => {
  return /^\S.* .*\S$/.test(value)
}

const noMaliciousPatterns = (value: string) => {
  return !/([$[\]{}\r\n])/.test(value) // eslint-disable-line no-useless-escape
}

const isValidForXML = (value: string) => {
  return !/[<>/\\"]/.test(value) // eslint-disable-line no-useless-escape
}

const dectPasswordValidation = (value: string) => {
  return /^(?!.*(.)\1{2})(?=(?:.*[a-z]){1})(?=(?:.*[A-Z]){1})(?=(?:.*\d){1})(?=(?:.*[^a-zA-Z\d]){1})(?!.*(012|123|234|345|456|567|678|789|890))(?!.*[<>/\\]).{9,127}$/.test(
    value
  )
}

const isValidFqdn = (value: string) => {
  if (!value) {
    return true
  }
  // FQDN regex pattern that validates:
  // - Domain labels separated by dots
  // - Each label can contain letters, numbers, and hyphens
  // - Labels cannot start or end with hyphens
  // - Total length cannot exceed 253 characters
  // - Each label cannot exceed 63 characters
  // - Must have at least one dot (to be a FQDN, not just a hostname)
  const fqdnRegex =
    /^(?=.{1,253}$)(?=.*\.)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/
  return fqdnRegex.test(value.trim())
}

export {
  atLeastOneCapitalLetter,
  atLeastOneDigit,
  atLeastOneLowercaseLetter,
  atLeastOneNotEmpty,
  atLeastOneSpecialCharacterExcept$,
  atLeastOneSpecialCharacterExcept$AndAccents,
  atLeastOneSpecialCharacterExceptHash,
  firstDigitInRange,
  isChecked,
  isGermanMobileNumber,
  isValidEuropeanMobileNumber,
  isNationalDestinationCode,
  isSubscriberNumber,
  isValidFileFormat,
  isValidEmailAddress,
  isValidFqdn,
  mustBePhoneNumber,
  mustContainIp4Or6Address,
  mustContainIpAddress,
  mustContainNoPrivateIpAddress,
  noAccents,
  noDuplicateEntry,
  requiredSelection,
  validPromptName,
  firstOrLastNameWithTitle,
  notAllowedSpecialCharacters,
  notEmptyAndMinLength,
  notPattern,
  getEmergencyNumbersRegex,
  noMaliciousPatterns,
  isGigaseMacId,
  isValidMacAddress,
  dectPasswordValidation,
  isValidForXML,
  atLeastOneWhiteSpace,
  noConnectMeIncompatibleCharacters,
}
